import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Text } from '../components/Texte';
import * as WebBrowser from 'expo-web-browser';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArticleCard } from '../components/ArticleCard';
import { CONTENU_BORNE } from '../navigation/screenOptions';
import { useNewsFeed } from '../hooks/useNewsFeed';
import { articleFromRow, useNewsFavorites } from '../hooks/useNewsFavorites';
import { useNewsLikes } from '../hooks/useNewsLikes';
import { ARTICLE_BASE_URL } from '../config/news';
import { t } from '../i18n';
import {
  colonnesGrille,
  colors,
  layout,
  radius,
  screenPadding,
  shadow,
  spacing,
  typography,
} from '../theme/tokens';
import type { NewsArticle } from '../types';

/**
 * Onglet « News » — fil d'articles d'alternjob.com, avec favoris.
 *
 * Racine d'onglet, donc pas de flèche de retour : il n'y a pas de pile à dépiler.
 *
 * « POUR TOI » EST CHRONOLOGIQUE PUR : les articles s'affichent dans l'ordre rendu par l'API,
 * sans aucun tri ni pondération côté client. Le segment nomme la destination, il ne promet pas un
 * algorithme — et tant qu'il n'y en a pas, il ne faut pas en simuler un.
 *
 * PÉRIMÈTRE : fil, éditeur, favoris, likes. Les champs `sponsored`, `cta`, `author`, `cover` et
 * `image` existent dans l'API et sont ignorés volontairement, jusque dans les types — un champ
 * qu'on ne veut pas afficher ne doit pas être à portée de main.
 *
 * LIKE ET FAVORI NE SONT PAS LA MÊME CHOSE, et c'est pour cela qu'ils vivent dans deux tables et
 * deux crochets : le like est un signal PUBLIC dont le nombre s'affiche à tous, le favori une liste
 * de lecture PRIVÉE. Les fusionner ferait d'un geste de lecture personnel une déclaration visible.
 */
type Segment = 'forYou' | 'favorites';

/** Nombre de squelettes au premier chargement : de quoi remplir un écran sans en promettre plus. */
const SQUELETTES = 3;

export function NewsScreen() {
  const [segment, setSegment] = useState<Segment>('forYou');
  const feed = useNewsFeed();
  const favoris = useNewsFavorites();

  /**
   * L'ÉTAT DES LIKES EST DEMANDÉ PAR PAQUET, pour les articles à l'écran — fil et favoris réunis.
   * Un favori ancien n'est pas dans le fil : sans son identifiant ici, son cœur resterait vide et
   * son compteur à zéro alors qu'il a peut-être vingt likes.
   */
  const idsVisibles = useMemo(
    () => [...new Set([...feed.articles.map((a) => a.id), ...favoris.rows.map((r) => r.article_id)])],
    [feed.articles, favoris.rows],
  );
  const likes = useNewsLikes(idsVisibles);

  /**
   * L'API D'ABORD, LA COPIE ENSUITE.
   *
   * La copie en base est un repli : elle sert quand la pagination n'a pas encore atteint l'article,
   * ce qui est le cas courant pour un favori ancien. Dès que le fil chargé contient cet id, c'est sa
   * version qui gagne — un titre corrigé depuis la mise en favori doit se voir.
   */
  const parId = useMemo(() => new Map(feed.articles.map((a) => [a.id, a])), [feed.articles]);
  const articlesFavoris = useMemo(
    () => favoris.rows.map((row) => parId.get(row.article_id) ?? articleFromRow(row)),
    [favoris.rows, parId],
  );

  const basculer = useCallback((article: NewsArticle) => void favoris.basculer(article), [favoris]);
  const aimer = useCallback((article: NewsArticle) => void likes.basculer(article.id), [likes]);

  /**
   * L'article se lit sur alternjob.com : l'API ne renvoie pas d'URL, elle se construit depuis le
   * slug — comme le fait déjà `services/content.ts` pour l'encart. Sans slug, rien à ouvrir, et la
   * carte ne fait rien plutôt que d'ouvrir une page d'erreur.
   *
   * NAVIGATEUR INTÉGRÉ, PAS `Linking.openURL`. Ce dernier éjecte vers Safari ou Chrome : au retour,
   * l'application repart de zéro et la position dans le fil est perdue — d'autant plus coûteux que
   * le fil est paginé et qu'on peut être à la quatrième page. Le navigateur intégré se referme d'un
   * geste et laisse l'écran exactement où il était. `expo-web-browser` est déjà embarqué et sert
   * déjà à l'OAuth (`hooks/useAuth.ts`) et à LinkedIn.
   */
  const ouvrir = useCallback((article: NewsArticle) => {
    if (!article.slug) return;
    WebBrowser.openBrowserAsync(`${ARTICLE_BASE_URL}/${article.slug}`).catch(() => undefined);
  }, []);

  /**
   * COLONNES — calculées sur la largeur RÉELLEMENT disponible, pas sur celle de la fenêtre : la
   * liste est rendue dans le conteneur borné et amputée de la marge latérale des deux côtés.
   * Compter sur la fenêtre donnerait une colonne de trop sur les très grands écrans, et des cartes
   * trop étroites.
   */
  const { width: largeurFenetre } = useWindowDimensions();
  const colonnes = colonnesGrille(
    Math.min(largeurFenetre, layout.contentMaxWidth) - screenPadding * 2,
  );

  const rendre = useCallback(
    ({ item }: { item: NewsArticle }) => {
      const carte = (
        <ArticleCard
          article={item}
          favorite={favoris.ids.has(item.id)}
          onToggleFavorite={basculer}
          liked={likes.mine.has(item.id)}
          likeCount={likes.counts[item.id] ?? 0}
          onToggleLike={aimer}
          onOpen={ouvrir}
          canInteract={favoris.connecte}
        />
      );
      // EN UNE COLONNE, AUCUNE ENVELOPPE : l'arbre rendu est exactement celui d'avant ce lot. Le
      // `flex: 1` n'a de sens qu'en ligne, pour que deux ou trois cartes se partagent la largeur à
      // parts égales — posé en colonne unique, il serait au mieux inutile, au pire un changement.
      return colonnes > 1 ? <View style={styles.cellule}>{carte}</View> : carte;
    },
    [aimer, basculer, colonnes, favoris.connecte, favoris.ids, likes.counts, likes.mine, ouvrir],
  );

  const donnees = segment === 'forYou' ? feed.articles : articlesFavoris;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* BANDE D'EN-TÊTE — le fond et le filet vont d'un bord à l'autre, le texte reste en colonne.
          C'est ce qui transforme les marges latérales en respiration plutôt qu'en vide : l'œil voit
          une bande traverser la page, et non un bloc flottant au milieu. */}
      <View style={styles.bandeEntete}>
        <View style={[styles.entete, CONTENU_BORNE]}>
          <Text style={styles.titre}>{t('news.title')}</Text>
          <View style={styles.segments} testID="news-segments">
          {(['forYou', 'favorites'] as Segment[]).map((cle) => (
            <Pressable
              key={cle}
              testID={`news-segment-${cle}`}
              accessibilityRole="tab"
              accessibilityState={{ selected: segment === cle }}
              onPress={() => setSegment(cle)}
              style={[styles.segment, segment === cle && styles.segmentActif]}
            >
              <Text style={[styles.segmentTexte, segment === cle && styles.segmentTexteActif]}>
                {t(`news.segments.${cle}`)}
              </Text>
            </Pressable>
            ))}
          </View>
        </View>
      </View>

      {/* Message d'échec du favori : DISCRET et non bloquant. L'action a déjà été annulée à
          l'écran ; une alerte modale pour un signet serait hors de proportion. */}
      {favoris.erreur ? (
        <Pressable testID="news-favorite-error" onPress={favoris.effacerErreur} style={styles.avis}>
          <Text style={styles.avisTexte}>{t('news.favorite.failed')}</Text>
        </Pressable>
      ) : null}

      {likes.erreur ? (
        <Pressable testID="news-like-error" onPress={likes.effacerErreur} style={styles.avis}>
          <Text style={styles.avisTexte}>{t('news.like.failed')}</Text>
        </Pressable>
      ) : null}

      <FlatList
        testID="news-list"
        // `key` LIÉE AU NOMBRE DE COLONNES, et ce n'est pas facultatif : react-native lève
        // « Changing numColumns on the fly is not supported » si la valeur change sur une liste
        // montée. Sur mobile elle vaut toujours 1, donc la clé ne bouge jamais.
        key={`grille-${colonnes}`}
        numColumns={colonnes}
        // Invariant react-native : `columnWrapperStyle` est REFUSÉ sur une liste à une colonne.
        columnWrapperStyle={colonnes > 1 ? styles.rangee : undefined}
        data={donnees}
        keyExtractor={(item) => item.id}
        renderItem={rendre}
        contentContainerStyle={[styles.liste, CONTENU_BORNE]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          segment === 'forYou' ? (
            <RefreshControl refreshing={feed.refreshing} onRefresh={feed.rafraichir} />
          ) : undefined
        }
        onEndReachedThreshold={0.5}
        onEndReached={segment === 'forYou' ? feed.chargerPlus : undefined}
        ListEmptyComponent={
          <EtatVide segment={segment} feed={feed} favoris={favoris} />
        }
        ListFooterComponent={
          segment === 'forYou' && donnees.length > 0 ? (
            <PiedListe loading={feed.loadingMore} exhausted={feed.exhausted} />
          ) : null
        }
      />
    </SafeAreaView>
  );
}

function PiedListe({ loading, exhausted }: { loading: boolean; exhausted: boolean }) {
  if (loading) {
    return (
      <View style={styles.pied} testID="news-loading-more">
        <ActivityIndicator color={colors.electric} />
      </View>
    );
  }
  if (exhausted) {
    return (
      <View style={styles.pied} testID="news-end">
        <Text style={styles.piedTexte}>{t('news.end')}</Text>
      </View>
    );
  }
  return null;
}

function Squelettes() {
  return (
    <View style={styles.squelettes} testID="news-skeletons">
      {Array.from({ length: SQUELETTES }, (_, i) => (
        <View key={i} style={styles.squelette} testID={`news-skeleton-${i}`}>
          <View style={styles.squeletteLigneCourte} />
          <View style={styles.squeletteImage} />
          <View style={styles.squeletteLigne} />
          <View style={styles.squeletteLigneCourte} />
        </View>
      ))}
    </View>
  );
}

function Bloc({ titre, corps, testID }: { titre: string; corps: string; testID: string }) {
  return (
    <View style={styles.bloc} testID={testID}>
      <Text style={styles.blocTitre}>{titre}</Text>
      <Text style={styles.blocTexte}>{corps}</Text>
    </View>
  );
}

function EtatVide({
  segment,
  feed,
  favoris,
}: {
  segment: Segment;
  feed: ReturnType<typeof useNewsFeed>;
  favoris: ReturnType<typeof useNewsFavorites>;
}) {
  if (segment === 'favorites') {
    // NON CONNECTÉ N'EST PAS UNE ERREUR : on invite, on n'alerte pas.
    if (!favoris.connecte) {
      return (
        <Bloc
          testID="news-favorites-signed-out"
          titre={t('news.favoritesSignedOutTitle')}
          corps={t('news.favoritesSignedOutBody')}
        />
      );
    }
    return (
      <Bloc
        testID="news-favorites-empty"
        titre={t('news.favoritesEmptyTitle')}
        corps={t('news.favoritesEmptyBody')}
      />
    );
  }

  if (feed.status === 'loading') return <Squelettes />;

  if (feed.status === 'error') {
    return (
      <View style={styles.bloc} testID="news-error">
        <Text style={styles.blocTitre}>{t('news.errorTitle')}</Text>
        <Text style={styles.blocTexte}>{t('news.errorBody')}</Text>
        <Pressable
          testID="news-retry"
          accessibilityRole="button"
          onPress={feed.reessayer}
          style={styles.reessayer}
        >
          <Text style={styles.reessayerTexte}>{t('common.retry')}</Text>
        </Pressable>
      </View>
    );
  }

  return <Bloc testID="news-empty" titre={t('news.emptyTitle')} corps={t('news.emptyBody')} />;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  // SURFACE de la bande : pleine largeur, c'est elle qui porte fond et filet.
  bandeEntete: {
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey200,
    paddingBottom: spacing.md,
  },
  // COLONNE de l'en-tête : bornée par CONTENU_BORNE à l'usage, rembourrages inchangés.
  entete: { paddingHorizontal: screenPadding, paddingTop: spacing.lg, gap: spacing.md },
  titre: {
    fontSize: typography.display.size,
    fontWeight: typography.display.weight,
    color: colors.navy,
  },
  segments: { flexDirection: 'row', gap: spacing.sm },
  segment: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.pill,
    backgroundColor: colors.grey100,
  },
  segmentActif: { backgroundColor: colors.navy },
  segmentTexte: {
    fontSize: typography.small.size,
    fontWeight: typography.bodyMd.weight,
    color: colors.grey700,
  },
  segmentTexteActif: { color: colors.white },

  avis: {
    marginHorizontal: screenPadding,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.corailSoft,
  },
  avisTexte: { fontSize: typography.small.size, color: colors.grey700 },

  liste: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },

  // Grille : la rangée porte l'écart horizontal, la cellule le partage à parts égales.
  rangee: { gap: spacing.lg },
  cellule: { flex: 1 },

  pied: { paddingVertical: spacing.lg, alignItems: 'center' },
  piedTexte: { fontSize: typography.small.size, color: colors.grey500 },

  squelettes: { gap: spacing.lg },
  squelette: {
    padding: spacing.lg,
    gap: spacing.md,
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  squeletteImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: radius.lg,
    backgroundColor: colors.grey100,
  },
  squeletteLigne: { height: spacing.lg, borderRadius: radius.sm, backgroundColor: colors.grey100 },
  squeletteLigneCourte: {
    height: spacing.md,
    width: '45%',
    borderRadius: radius.sm,
    backgroundColor: colors.grey100,
  },

  bloc: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  blocTitre: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night },
  blocTexte: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
  },
  reessayer: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  reessayerTexte: {
    fontSize: typography.bodyMd.size,
    fontWeight: typography.h3.weight,
    color: colors.electric,
  },
});
